/**
 * @file Auth API helpers: login/register/session, password flows, invites, Google OAuth redirect.
 * Uses the shared credentialed client (cookies + CSRF); no tokens in localStorage.
 * @module frontend/src/api/auth
 */

import client from './client';
import { APP_HOME } from '../constants';

/** Public auth feature flags (registration, Google). */
export async function getAuthConfig() {
  const { data } = await client.get('/auth/config');
  return data;
}

/** Current session from access cookie (/auth/me). */
export async function getMe() {
  const { data } = await client.get('/auth/me');
  return data;
}

/**
 * Email/password login; sets session cookies via Set-Cookie.
 * @param {{ email: string, password: string }} credentials
 */
export async function login({ email, password }) {
  const { data } = await client.post('/auth/login', { email, password });
  return data;
}

/**
 * Direct register (legacy/alternate path); prefer request/verify code flow in UI.
 * @param {object} payload
 */
export async function register(payload) {
  const { data } = await client.post('/auth/register', payload);
  return data;
}

/**
 * Start email verification signup (stores pending registration server-side).
 * @param {object} payload
 */
export async function requestRegisterCode(payload) {
  const { data } = await client.post('/auth/register/request-code', payload);
  return data;
}

/**
 * Complete signup with email OTP; returns session payload.
 * @param {{ email: string, code: string }} args
 */
export async function verifyRegisterCode({ email, code }) {
  const { data } = await client.post('/auth/register/verify', { email, code });
  return data;
}

/** Clear session cookies on the server. */
export async function logout() {
  const { data } = await client.post('/auth/logout');
  return data;
}

/** Exchange refresh cookie for a new access session. */
export async function refreshSession() {
  const { data } = await client.post('/auth/refresh');
  return data;
}

/**
 * Request a password-reset email.
 * @param {string} email
 */
export async function forgotPassword(email) {
  const { data } = await client.post('/auth/forgot-password', { email });
  return data;
}

/**
 * Complete password reset with token from email link.
 * @param {{ token: string, newPassword: string }} args
 */
export async function resetPassword({ token, newPassword }) {
  const { data } = await client.post('/auth/reset-password', { token, newPassword });
  return data;
}

/**
 * Change password while authenticated.
 * @param {{ currentPassword: string, newPassword: string }} args
 */
export async function changePassword({ currentPassword, newPassword }) {
  const { data } = await client.post('/auth/change-password', { currentPassword, newPassword });
  return data;
}

/**
 * Update display name on /auth/me.
 * @param {{ name: string }} args
 */
export async function updateProfile({ name }) {
  const { data } = await client.patch('/auth/me', { name });
  return data;
}

/**
 * Accept a company invitation (token + account fields).
 * @param {object} payload
 */
export async function acceptInvite(payload) {
  const { data } = await client.post('/auth/accept-invite', payload);
  return data;
}

/**
 * Preview invite metadata for the accept-invite UI.
 * @param {string} token
 */
export async function getInvitePreview(token) {
  const { data } = await client.get(`/auth/invite/${encodeURIComponent(token)}`);
  return data;
}

/**
 * Finish company naming after signup when company is still missing.
 * @param {{ companyName: string }} args
 */
export async function completeCompany({ companyName }) {
  const { data } = await client.post('/auth/complete-company', { companyName });
  return data;
}

/**
 * Full-page redirect to Google OAuth start URL.
 * @param {{ returnUrl?: string, inviteToken?: string }} [opts]
 */
export function startGoogleLogin({ returnUrl = APP_HOME, inviteToken = '' } = {}) {
  const base = process.env.REACT_APP_API_URL || '/api';
  const params = new URLSearchParams();
  // REVIEW [MEDIUM]: returnUrl is forwarded to the API without sanitization here —
  // callers (Login/Register) should pass same-origin paths; backend must reject open redirects.
  if (returnUrl) params.set('returnUrl', returnUrl);
  if (inviteToken) params.set('inviteToken', inviteToken);
  window.location.href = `${base}/auth/google?${params.toString()}`;
}
